/**
 * DriftDetectionBanner — shows when Act I foundation changes after committing to draft.
 *
 * Monitors variables, characters, and plot_extraction for changes that
 * post-date the Act I commit timestamp. When drift is detected a sticky
 * amber banner appears at the top of the workspace with a "Rebuild Advisor"
 * panel that guides the writer on reconciling their draft.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, X, ChevronDown, ChevronUp, RefreshCw, BookOpen, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePW } from '@/lib/store';
import { getAct1CommittedKey } from './dialogs/CommitGateDialog';

/* ── helpers ─────────────────────────────────────────────────────── */

function getCommitTimestamp(projectId) {
  if (!projectId) return null;
  try {
    const val = localStorage.getItem(getAct1CommittedKey(projectId));
    return val ? Number(val) : null;
  } catch { return null; }
}

function snapshotKey(projectId) {
  return `cr_act1_snapshot_${projectId}`;
}

function saveSnapshot(projectId, snapshot) {
  try {
    localStorage.setItem(snapshotKey(projectId), JSON.stringify(snapshot));
  } catch { /* ok */ }
}

function loadSnapshot(projectId) {
  try {
    const raw = localStorage.getItem(snapshotKey(projectId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function buildSnapshot({ characters, variables, plotExtraction }) {
  return {
    characterIds: (characters || []).map((c) => c.id || c._id).sort(),
    characterCount: (characters || []).length,
    variableKeys: Object.keys(variables || {}).sort(),
    hasPlot: !!(plotExtraction?.extracted_at),
    plotStructure: plotExtraction?.structure_type || null,
  };
}

function detectDrift(old, next) {
  if (!old || !next) return [];
  const diffs = [];

  if (old.characterCount !== next.characterCount) {
    const delta = next.characterCount - old.characterCount;
    diffs.push({
      area: 'Characters',
      detail: delta > 0
        ? `${delta} new character${delta > 1 ? 's' : ''} added since drafting began`
        : `${Math.abs(delta)} character${Math.abs(delta) > 1 ? 's' : ''} removed since drafting began`,
    });
  }

  const addedVars = next.variableKeys.filter((k) => !old.variableKeys.includes(k));
  const removedVars = old.variableKeys.filter((k) => !next.variableKeys.includes(k));
  if (addedVars.length || removedVars.length) {
    diffs.push({
      area: 'Story Variables',
      detail: [
        addedVars.length && `${addedVars.length} variable${addedVars.length > 1 ? 's' : ''} added`,
        removedVars.length && `${removedVars.length} variable${removedVars.length > 1 ? 's' : ''} removed`,
      ].filter(Boolean).join(', '),
    });
  }

  if (old.plotStructure && next.plotStructure && old.plotStructure !== next.plotStructure) {
    diffs.push({
      area: 'Plot Structure',
      detail: `Structure changed from "${old.plotStructure}" to "${next.plotStructure}"`,
    });
  }

  return diffs;
}

/* ── Rebuild Advisor panel ───────────────────────────────────────── */

function RebuildAdvisor({ drifts, onDismiss, onRecommit }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div
      data-testid="drift-advisor-panel"
      className="fixed bottom-6 right-4 z-50 w-[340px] rounded-xl border border-amber-500/30 bg-[hsl(var(--chrome))] shadow-2xl shadow-amber-500/10 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center gap-2 bg-amber-500/10 px-4 py-2.5 border-b border-amber-500/20">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
        <span className="flex-1 text-[12px] font-bold text-amber-300">Rebuild Advisor</span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-amber-400/60 hover:text-amber-300 transition-colors"
          data-testid="drift-advisor-toggle-btn"
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="text-amber-400/60 hover:text-amber-300 transition-colors ml-1"
          data-testid="drift-advisor-close-btn"
          title="Dismiss (drift will re-appear if more changes are made)"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="px-4 py-3 flex flex-col gap-3">
          <p className="text-[11.5px] text-muted-foreground leading-relaxed">
            Your story foundation has changed since you began drafting. Review these areas
            and decide whether to update your draft or re-lock your outline.
          </p>

          {/* Drift list */}
          <div className="flex flex-col gap-1.5">
            {drifts.map((d, i) => (
              <div key={i} className="flex items-start gap-2 rounded-md bg-amber-500/8 border border-amber-500/15 px-3 py-2">
                <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                <div>
                  <span className="text-[11.5px] font-semibold text-amber-300">{d.area}: </span>
                  <span className="text-[11px] text-muted-foreground">{d.detail}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Suggested actions</p>

            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('loom:open-arc-walker'))}
              className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-[11.5px] text-foreground hover:bg-secondary transition-colors"
              data-testid="drift-open-arc-walker-btn"
            >
              <BookOpen className="h-3.5 w-3.5 text-primary shrink-0" />
              Run Arc Walker — re-score your draft
            </button>

            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('loom:open-edit-walker'))}
              className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-[11.5px] text-foreground hover:bg-secondary transition-colors"
              data-testid="drift-open-edit-walker-btn"
            >
              <RefreshCw className="h-3.5 w-3.5 text-blue-400 shrink-0" />
              Run Edit Walker — align draft to new outline
            </button>

            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'editor' }))}
              className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-[11.5px] text-foreground hover:bg-secondary transition-colors"
              data-testid="drift-open-editor-btn"
            >
              <PenLine className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
              Keep drafting — I'll reconcile manually
            </button>

            <button
              type="button"
              onClick={onRecommit}
              className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11.5px] font-semibold text-amber-300 hover:bg-amber-500/20 transition-colors"
              data-testid="drift-recommit-btn"
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Accept changes — re-lock foundation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main export ──────────────────────────────────────────────────── */

export function DriftDetectionBanner() {
  const { project, characters = [], variables = {} } = usePW();
  const projectId = project?.id;
  const plotExtraction = project?.plot_extraction;

  const [drifts, setDrifts]       = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const prevSnapshotRef           = useRef(null);

  // Only active when Act I has been committed
  const commitTs = useMemo(() => getCommitTimestamp(projectId), [projectId]);
  const isActive = !!commitTs;

  // On first mount (or project switch), save the baseline snapshot if
  // none exists yet (i.e. the moment Act I was committed).
  useEffect(() => {
    if (!projectId || !isActive) return;
    const existing = loadSnapshot(projectId);
    if (!existing) {
      const snap = buildSnapshot({ characters, variables, plotExtraction });
      saveSnapshot(projectId, snap);
      prevSnapshotRef.current = snap;
    } else {
      prevSnapshotRef.current = existing;
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whenever characters/variables/plot change, compare to baseline
  useEffect(() => {
    if (!projectId || !isActive) return;
    const baseline = prevSnapshotRef.current || loadSnapshot(projectId);
    if (!baseline) return;
    const current = buildSnapshot({ characters, variables, plotExtraction });
    const found = detectDrift(baseline, current);
    setDrifts(found);
    setDismissed(false); // new changes re-surface the banner
  }, [characters, variables, plotExtraction, projectId, isActive]);

  const handleDismiss = () => setDismissed(true);

  const handleRecommit = () => {
    // Save current state as the new baseline
    if (projectId) {
      const snap = buildSnapshot({ characters, variables, plotExtraction });
      saveSnapshot(projectId, snap);
      prevSnapshotRef.current = snap;
    }
    setDrifts([]);
    setDismissed(true);
  };

  const hasDrift = drifts.length > 0 && !dismissed;

  if (!isActive || !hasDrift) return null;

  return (
    <>
      {/* Slim top banner */}
      <div
        data-testid="drift-detection-banner"
        className="flex shrink-0 items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5"
        role="alert"
      >
        <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
        <span className="flex-1 text-[11.5px] text-amber-300">
          <strong className="font-bold">Foundation drift detected</strong>
          {' '}— your story outline has changed since you began drafting.{' '}
          <button
            type="button"
            onClick={handleDismiss}
            className="underline underline-offset-2 hover:text-amber-200 transition-colors"
            data-testid="drift-banner-advisor-link"
          >
            See Rebuild Advisor ↘
          </button>
        </span>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 text-amber-400/60 hover:text-amber-300 transition-colors"
          title="Dismiss"
          data-testid="drift-banner-dismiss-btn"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Floating advisor panel */}
      <RebuildAdvisor
        drifts={drifts}
        onDismiss={handleDismiss}
        onRecommit={handleRecommit}
      />
    </>
  );
}
